import qs from 'querystring'

import { isMobilePhone } from '@openhouse-technologies/utils'
import dayjs from 'dayjs'
import EventEmitter from 'eventemitter3'
import { isEmpty, isObject, isUndefined, get, isNull } from 'lodash'
import ReactDOM from 'react-dom'
import { store as notificationStore } from 'react-notifications-component'
import Swal from 'sweetalert2'
// Vite resolves 'sweetalert2' to its unstyled build (CRA used the one with
// the CSS inside). Without this, every toast lands as bare text at the far
// left of the page, hidden under the sidebar (Satya's clips, 23 Oct).
import 'sweetalert2/dist/sweetalert2.min.css'
import Cookies from 'universal-cookie'

import { getNewAccessToken as getNewAccessTokenApi, fetchUser } from 'api'
import {
  OPENDAY_SLOTS_MINUTE_INTERVAL,
  bulkMessagingFilterTypes
} from 'app/config'
import constants, { RECHARGE_REASON } from 'app/constants'
import { decodeJWT, isTokenExpired } from 'app/utils'
import { sentryLogger } from 'sentry'

const _eventEmitter = new EventEmitter()
export const eventEmitter = {
  addListener: (event, func) => _eventEmitter.addListener(event, func),
  removeListener: (event, func) => _eventEmitter.removeListener(event, func),
  emit: (event, data) => _eventEmitter.emit(event, data)
}

// freezing eventEmitter, so that later it won't get modified by any chance
Object.freeze(eventEmitter)

export const cookies = new Cookies()

const isLocalHost =
  typeof window !== 'undefined' &&
  ['localhost', '127.0.0.1'].includes(window.location.hostname)

const defaultCookieOption = {
  path: '/',
  secure: !isLocalHost && process.env.NODE_ENV !== 'development',
  maxAge: 30 * 86400 // 30 days
}

const subdomainCookieOption = {
  sameSite: 'lax',
  ...(!isLocalHost && process.env.NODE_ENV !== 'development'
    ? { domain: '.openhouse.study' }
    : null)
}

const cookieOption = {
  ...defaultCookieOption,
  ...subdomainCookieOption
}

export const getCookie = (key) => {
  return cookies.get(key)
}

export const setCookie = (key, value) => {
  return cookies.set(key, value, cookieOption)
}

export const removeCookie = (key) => {
  cookies.remove(key, cookieOption)
  /**
   * try to remove cookie with defaultCookieOption as fallback
   * bcz remove config should be same as create config
   */
  cookies.remove(key, defaultCookieOption)
}

export const refreshAllCookies = () => {
  const allCookies = cookies.getAll()
  console.log('refreshing all cookies :- ', allCookies)

  for (const [cookieName, cookieValue] of Object.entries(allCookies)) {
    removeCookie(cookieName)
    setCookie(cookieName, cookieValue)
  }
}

export const getAccessToken = () => {
  const token = getCookie(constants.ACCESS_TOKEN_KEY)

  if (token && !isTokenExpired(token)) {
    return token
  }
}

export const setAccessToken = (value, name) => {
  setCookie(name, value)
}

export const setAccessTokenKey = (type) => {
  return `${type.toUpperCase()}-TOKEN-${process.env.REACT_APP_CUSTOM_NODE_ENV.toUpperCase()}`
}

export const redirectToOrigin = () => {
  window.location.href = window.location.origin
}

export const clearStorage = () => {
  try {
    const keys = [
      constants.ACCESS_TOKEN_KEY,
      constants.REFRESH_TOKEN_KEY,
      constants.REFRESH_TOKEN_LAST_UPDATED
    ]

    keys.forEach((key) => removeCookie(key))
  } catch (error) {
    console.log(error)
  }
}

export const getNewAccessToken = async () => {
  const accessToken = getAccessToken()
  const refreshToken = getCookie(constants.REFRESH_TOKEN_KEY)
  const refreshTokenLastUpdated = getCookie(
    constants.REFRESH_TOKEN_LAST_UPDATED
  )

  if (accessToken && refreshToken && refreshTokenLastUpdated) {
    // calculate difference in days
    const lastUpdatedAgoInDays = dayjs().diff(
      new dayjs(refreshTokenLastUpdated),
      'days'
    )

    // fetch new token if its older than 25 days
    if (lastUpdatedAgoInDays > 25) {
      try {
        const response = await getNewAccessTokenApi({
          refresh_token: refreshToken
        })

        setAccessToken(response.access_token, constants.ACCESS_TOKEN_KEY)
        setAccessToken(response.refresh_token, constants.REFRESH_TOKEN_KEY)
        setCookie(
          constants.REFRESH_TOKEN_LAST_UPDATED,
          dayjs().format('YYYY/MM/DD HH:mm:ss')
        )
        return response.access_token
      } catch (error) {
        console.log('error in getting new access token :- ', error.message)
        sentryLogger(error)
        throw error
      }
    } else {
      return accessToken
    }
  }
}

export const isUserAuthenticated = () => {
  const accessToken = getCookie(constants.ACCESS_TOKEN_KEY)

  if (accessToken) {
    return isTokenExpired(accessToken) === false
  }
  return false
}

export const getLoggedInUserId = () => {
  try {
    const accessToken = decodeJWT(getAccessToken())

    if (accessToken && !isTokenExpired(accessToken)) {
      return accessToken.sub
    }
  } catch (error) {
    sentryLogger(error, 'error in getting logged in user id')
  }
}

export const getLoggedInUserName = () => {
  try {
    const accessTokenString = getAccessToken()
    const accessToken = decodeJWT(accessTokenString)

    if (accessToken && !isTokenExpired(accessTokenString)) {
      return accessToken.name
    }
  } catch (error) {
    console.log('error in getting logged in user name :- ', error)
  }
}

export const checkIfNetworkError = (error) => {
  return (
    error.isAxiosError &&
    isUndefined(error.response) &&
    error.message === constants.NETWORK_ERROR
  )
}

export const getNetworkResponseError = (error) => {
  const defaultError = {
    isError: true,
    fieldErrors: {},
    errorMessage: ''
  }

  try {
    if (checkIfNetworkError(error)) {
      return {
        ...defaultError,
        errorMessage: constants.NETWORK_ERROR
      }
    } else {
      if (error.response.data.error) {
        const errorMessage =
          error.response.data.errorMessage || error.response.data.message

        if (!isEmpty(errorMessage)) {
          return {
            ...defaultError,
            errorMessage
          }
        }
      } else if (!isEmpty(error.response.data)) {
        const fieldErrors = {}

        for (const [key, value] of Object.entries(error.response.data)) {
          fieldErrors[key] = Array.isArray(value) ? value.join('\n') : value
        }

        return {
          ...defaultError,
          errorMessage: '',
          fieldErrors
        }
      }
    }

    return defaultError
  } catch (error) {
    // returning defaultError in case of any exception above
    sentryLogger(error)
    return defaultError
  }
}

export const apiFormattedResult = (data) => {
  if (data) {
    return {
      count: data.count,
      data: !isUndefined(data.results) ? data.results : data.data,
      previous: data.previous,
      next: data.next,
      ...(data.label ? { label: data.label } : null),
      ...(data.due ? { due: data.due } : null),
      ...(data.paid ? { paid: data.paid } : null)
    }
  }
}

const Toast = Swal.mixin({
  toast: true,
  position: 'top-end',
  showConfirmButton: false,
  timer: 3000,
  timerProgressBar: true,
  didOpen: (toast) => {
    toast.addEventListener('mouseenter', Swal.stopTimer)
    toast.addEventListener('mouseleave', Swal.resumeTimer)
  }
})

export const showToast = (message, type) => {
  if (type === constants.SUCCESS) {
    Toast.fire({
      icon: 'success',
      title: message
    })
  } else if (type === constants.ERROR) {
    Toast.fire({
      icon: 'error',
      title: message
    })
  } else if (type === constants.INFO) {
    Toast.fire({
      icon: 'info',
      title: message
    })
  }
}
/**
 *
 * @param {*} successFunc which will be called if yes is pressed
 * @param {*} message the confirm message
 */
// Brand-consistent confirmation popup. Previously used SweetAlert2's
// default styling, whose button DOM/CSS can't be made to match the
// MUI buttons used everywhere else in Alfred (modify-admission modal,
// cart "proceed to checkout", etc.). Render an actual MUI Dialog via
// ReactDOM so the buttons look like the rest of the portal.
//
// The helper API is unchanged — callers still invoke confirm(success,
// message, errorFunc, options) as before.
import ConfirmDialog from 'app/components/generic/ConfirmDialog'
import AlertDialog from 'app/components/generic/AlertDialog'

const _mountConfirmDialog = ({
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel
}) => {
  const container = document.createElement('div')
  document.body.appendChild(container)
  const cleanup = () => {
    try {
      ReactDOM.unmountComponentAtNode(container)
    } catch (e) {
      /* no-op */
    }
    if (container.parentNode) container.parentNode.removeChild(container)
  }
  ReactDOM.render(
    <ConfirmDialog
      open
      title={title}
      message={message}
      confirmText={confirmText}
      cancelText={cancelText}
      onConfirm={() => {
        cleanup()
        if (onConfirm) onConfirm()
      }}
      onCancel={() => {
        cleanup()
        if (onCancel) onCancel()
      }}
    />,
    container
  )
}

export const confirm = (
  successFunc,
  message = "You won't be able to revert this!",
  errorFunc,
  options = {}
) => {
  _mountConfirmDialog({
    title: options.title === undefined ? 'Are you sure?' : options.title,
    message,
    confirmText: options.confirmButtonText || 'Yes',
    cancelText: options.cancelButtonText || 'Cancel',
    onConfirm: successFunc,
    onCancel: errorFunc
  })
}

const _mountAlertDialog = ({ variant, title, message, buttonText }) => {
  const container = document.createElement('div')
  document.body.appendChild(container)
  const cleanup = () => {
    try {
      ReactDOM.unmountComponentAtNode(container)
    } catch (e) {
      /* no-op */
    }
    if (container.parentNode) container.parentNode.removeChild(container)
  }
  ReactDOM.render(
    <AlertDialog
      open
      variant={variant}
      title={title}
      message={message}
      buttonText={buttonText}
      onClose={cleanup}
    />,
    container
  )
}

export const eventAlert = (message, type, title) => {
  if (type === constants.SUCCESS) {
    _mountAlertDialog({
      variant: 'success',
      title: title || 'Success',
      message
    })
  } else if (type === constants.ERROR) {
    _mountAlertDialog({
      variant: 'error',
      title: title || 'Error',
      message
    })
  }
}

// Promise variant — resolves with { isConfirmed: bool } so callers can
// await it. Replaces the SweetAlert2 variant with the same MUI Dialog
// used by confirm() above.
export const confirmDialog = async ({
  text = "You won't be able to revert this!",
  confirmButtonText = 'Yes, delete it!',
  options = {}
}) => {
  return new Promise((resolve) => {
    _mountConfirmDialog({
      title: options.title === undefined ? 'Are you sure?' : options.title,
      message: text,
      confirmText: confirmButtonText,
      cancelText: options.cancelButtonText || 'Cancel',
      onConfirm: () => resolve({ isConfirmed: true, isDismissed: false }),
      onCancel: () => resolve({ isConfirmed: false, isDismissed: true })
    })
  })
}

export const alertDialog = async (options = {}) => {
  return new Promise((resolve) => {
    _mountAlertDialog({
      variant: options.icon || 'warning',
      title: options.title === undefined ? 'Alert' : options.title,
      message: options.text || 'This is an alert',
      buttonText: options.confirmButtonText || 'OK'
    })
    // Existing callers only fire-and-forget; resolve immediately so any
    // awaiting code doesn't hang. The dialog dismisses itself on OK
    // click.
    resolve()
  })
}

export const makeRequestUrl = (url, queries) => {
  if (!isEmpty(url) && !isEmpty(queries)) {
    const formattedQueries = {}
    Object.keys(queries).forEach((key) => {
      let queryPrefix = key
      if (queryPrefix.indexOf('.') > -1) {
        queryPrefix = queryPrefix.replace(/\./g, '__')
      }

      if (queries[key] !== undefined) {
        formattedQueries[queryPrefix] = queries[key]
      }
    })
    return `${url}${url.indexOf('?') > -1 ? '&' : '?'}${qs.stringify(
      formattedQueries
    )}`
  }
  return url
}

// below helpers taken from oh admin dashboard for login, modify later if needed
export function isNullOrEmpty(params, condition = 'or') {
  let isInvalid = false
  let counter = 0
  if (Array.isArray(params)) {
    if (params.length) {
      params.forEach((value, key) => {
        if (
          value === undefined ||
          value === null ||
          value === 0 ||
          value === false ||
          value.length === 0 ||
          value === ''
        ) {
          isInvalid = true
          counter++
        }
      })
    } else {
      isInvalid = true
    }
  } else if (
    params === undefined ||
    params === null ||
    params === '' ||
    params === 0 ||
    params === false
  ) {
    isInvalid = true
  }
  if (Array.isArray(params) && condition === 'and') {
    if (counter === params.length) {
      return true
    }
    return false
  }
  return isInvalid
}

export function isNullOrEmptyLodash(value) {
  return isNull(value) || isEmpty(value)
}

export function notify(title, message, type, duration = 3000) {
  try {
    message = message || ' '
    notificationStore.addNotification({
      title,
      message,
      type,
      insert: 'top',
      container: 'top-center',
      animationIn: ['animated', 'fadeIn'],
      animationOut: ['animated', 'fadeOut'],
      dismiss: {
        duration,
        onScreen: true
      },
      showIcon: false,
      pauseOnHover: true
    })
  } catch (e) {
    console.error(e)
  }
}

export const getJMName = () => localStorage.getItem('journey_mentor')

export const removeJMName = () => localStorage.removeItem('journey_mentor')

export const roundedDecimal = (value) => {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

export const computeBulkMessagingUserFiltersQueries = (userFilters) => {
  const getFilterName = {
    [bulkMessagingFilterTypes.CENTRE]: 'centre_uuids',
    [bulkMessagingFilterTypes.TEACHER]: 'teacher_uuids',
    [bulkMessagingFilterTypes.CATEGORY]: 'category',
    [bulkMessagingFilterTypes.SUBJECT]: 'subject_uuids'
  }

  const modifiedQueries = Object.fromEntries(
    Object.entries(userFilters)
      .filter(([_, value]) => !isEmpty(value))
      .map((item) => [
        getFilterName[item[0]],
        item[1].map((item) => (isObject(item) ? item.value : item)).join(',')
      ])
  )

  return modifiedQueries
}

export const modifyQueries = (queries = {}) => {
  if (queries?.auto_booking) {
    const autoBookingValMap = {
      in: true,
      out: false
    }
    queries.auto_booking = autoBookingValMap[queries.auto_booking.toLowerCase()]
  }

  return queries
}

export const userEditModalHandler = (path, uuid) => {
  window.open(`${window.location.origin}${path}?editMode=1&uuid=${uuid}`)
}

export const validateParentDetails = (details) => {
  const { centre_preference, user } = details || {}

  let isValid = false
  const invalidAttributes = []

  const isValidFirstName = !isEmpty(user?.first_name)
  const isValidLastName = !isEmpty(user?.last_name)
  const isValidMobile = isMobilePhone(user?.phone_number || '', 'en-IN')
  const isValidState = !isEmpty(user?.state)
  const isValidCentre = !isEmpty(centre_preference)

  if (
    isValidFirstName &&
    isValidLastName &&
    isValidMobile &&
    isValidState &&
    isValidCentre
  ) {
    isValid = true
  } else {
    isValid = false

    if (!isValidFirstName) invalidAttributes.push('first_name')
    if (!isValidLastName) invalidAttributes.push('last_name')
    if (!isValidMobile) invalidAttributes.push('phone_number')
    if (!isValidState) invalidAttributes.push('state')
    if (!isValidCentre) invalidAttributes.push('centre')
  }

  return {
    isValid,
    invalidAttributes
  }
}

export const createScheduleData = (date) => {
  const scheduleData = []
  const givenDate = new Date(date)
  for (let i = 0; i < 12; i++) {
    const nextDate = new Date(
      givenDate.getFullYear(),
      givenDate.getMonth() + i + 1,
      0
    )
    const month = (nextDate.getMonth() + 1).toString().padStart(2, '0')
    const day = nextDate.getDate().toString().padStart(2, '0')

    const dateData = {
      label: `${nextDate.getFullYear()}-${month}-${day}`,
      value: `${nextDate.getFullYear()}-${month}-${day}`
    }

    scheduleData.push(dateData)
  }

  return scheduleData
}

export const findMaxDate = (date1, date2) => {
  const [d1, d2] = [date1, date2].map(dayjs)
  return d1.isAfter(d2) ? d1 : d2
}

export const convertArrayToObject = (arr) => {
  return arr.reduce((obj, str) => {
    obj[str] = str
    return obj
  }, {})
}

export const fetchTeacher = async (searchedText) => {
  try {
    var numberReg = /^\d+$/
    var aplhaReg = /^[a-zA-Z ]*$/
    var uuidReg =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

    if (searchedText?.length >= 0) {
      let queries = {}

      if (numberReg.test(searchedText)) queries = { phone_number: searchedText }
      else if (aplhaReg.test(searchedText)) queries = { name: searchedText }
      else if (uuidReg.test(searchedText)) queries = { uuid: searchedText }

      const response = await fetchUser({ queries }, constants.TEACHER)

      if (response.results.length >= 0) {
        return response.results.map((item) => {
          const { uuid, full_name, phone_number } = item?.user

          return {
            value: uuid,
            name: `${full_name} (${phone_number})`
          }
        })
      } else if (response.results.length > 1 && searchedText.length === 10) {
        throw new Error('Multiple teacher found with same number')
      } else {
        return {
          value: '',
          name: `Teacher not found`
        }
      }
    }
  } catch (error) {
    console.log('Error in fetching teacher :- ', error)
    throw error
  }
}

export function flattenObjectArray(array, keysMap) {
  return array.map((element) => {
    let result = {}
    for (const [key, value] of Object.entries(keysMap)) {
      result[key] = get(element, value)
    }
    return result
  })
}

export const canAddReferralCode = (values) => {
  return (
    !isEmpty(values.addCoinsType) &&
    values.reason === RECHARGE_REASON.RECHARGE &&
    !values?.has_referred_by
  )
}

export const formatTimeString = (timeString) => {
  const [hours, minutes] = timeString?.split(':').map(Number) ?? [0, 0]
  const amPm = hours >= 12 ? 'pm' : 'am'
  const hour12 = hours % 12 === 0 ? 12 : hours % 12
  const formattedMinutes = minutes.toString().padStart(2, '0')
  return `${hour12}:${formattedMinutes} ${amPm}`
}

export const getOpendayTimeSlots = (start, end) => {
  const slots = {}
  const startingHour = start ? start.split(':')[0] : 0
  const endingHour = end ? end.split(':')[0] : 24
  let startingMinute = 0

  for (let hour = Number(startingHour); hour < Number(endingHour); hour++) {
    startingMinute = 0
    if (hour === Number(startingHour))
      startingMinute =
        Number(start?.split(':')[1]) + OPENDAY_SLOTS_MINUTE_INTERVAL
    for (
      let minute = startingMinute;
      minute < 60;
      minute += OPENDAY_SLOTS_MINUTE_INTERVAL
    ) {
      const key = `${hour.toString().padStart(2, '0')}:${minute
        .toString()
        .padStart(2, '0')}:00`
      const value = formatTimeString(key)
      slots[key] = value
    }
  }

  return slots
}

export const formatCadence = (cadence) => {
  if (!Array.isArray(cadence)) {
    return ''
  }

  // Sort by weekday
  const sortedCadence = cadence.sort((a, b) => (a.weekday > b.weekday ? 1 : -1))

  // If all days have the same timing
  const allSameTiming = sortedCadence.every(
    (item) =>
      item.start_time === sortedCadence[0].start_time &&
      item.end_time === sortedCadence[0].end_time
  )

  // if days are continuous
  const weekdays = sortedCadence.map((item) => item.weekday)
  const isContinuous = weekdays.every(
    (day, index) => index === 0 || day === weekdays[index - 1] + 1
  )

  if (allSameTiming && isContinuous && sortedCadence.length > 2) {
    const start = sortedCadence[0].start_time.split('-').join(':')
    const end = sortedCadence[0].end_time.split('-').join(':')
    const firstDay = dayjs()
      .day(
        sortedCadence[0].weekday >= 0 && sortedCadence[0].weekday < 7
          ? sortedCadence[0].weekday + 1
          : 7
      )
      .format('ddd')
    const lastDay = dayjs()
      .day(
        sortedCadence[sortedCadence.length - 1].weekday >= 0 &&
          sortedCadence[sortedCadence.length - 1].weekday < 7
          ? sortedCadence[sortedCadence.length - 1].weekday + 1
          : 7
      )
      .format('ddd')

    return `${firstDay}-${lastDay}, ${dayjs('1/1/1 ' + start).format(
      'h:mma'
    )} - ${dayjs('1/1/1 ' + end).format('h:mma')}`
  }

  // Default formatting for non-continuous days or different timings
  const formattedCadence = sortedCadence
    .map((item) => {
      const start = item.start_time.split('-').join(':')
      const end = item.end_time.split('-').join(':')
      return `${dayjs()
        .day(item.weekday >= 0 && item.weekday < 7 ? item.weekday + 1 : 7)
        .format('ddd')}, ${dayjs('1/1/1 ' + start).format('h:mma')} - ${dayjs(
        '1/1/1 ' + end
      ).format('h:mma')}`
    })
    .join(' | ')

  return formattedCadence
}

/** Clock times only from cadence (no weekdays). Deduplicates when the slot is the same every day. */
export const formatCadenceTimeOnly = (cadence) => {
  if (!Array.isArray(cadence) || cadence.length === 0) {
    return ''
  }

  const formatSlot = (startTime, endTime) => {
    const start = startTime.split('-').join(':')
    const end = endTime.split('-').join(':')
    return `${dayjs('1/1/1 ' + start).format('h:mma')}-${dayjs(
      '1/1/1 ' + end
    ).format('h:mma')}`
  }

  const seen = new Set()
  const uniqueSlots = []
  for (const item of cadence) {
    const key = `${item.start_time}\0${item.end_time}`
    if (!seen.has(key)) {
      seen.add(key)
      uniqueSlots.push([item.start_time, item.end_time])
    }
  }

  return uniqueSlots.map(([s, e]) => formatSlot(s, e)).join(' · ')
}

export const formatLevel = (level) => {
  if (level === '1') return 'Beginner'
  else if (level === '2') return 'Intermediate'
  else if (level === '3') return 'Advanced'
}

export const getMonthYear = (dateString) => {
  const dateObject = new Date(dateString)
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ]
  const monthIndex = dateObject.getMonth()
  const year = dateObject.getFullYear()
  return `${monthNames[monthIndex]} ${year}`
}
